// hooks/useChat.js
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { conversation } from "network/ApiAxios";
import { useChatContext } from 'contexts/ChatContext';
import config from 'core/config';
import { fetchEventSource } from '@microsoft/fetch-event-source';

// 🚀 SIMPLE SSE STREAMING - Show content as it arrives
const MIN_LOADING_TIME_MS = 800;

export const useChat = (id) => {
    const navigate = useNavigate();
    const { conversationsUpdated, refreshConversations } = useChatContext();
    const token = localStorage.getItem('token');

    // State
    const [messages, setMessages] = useState([]);
    const [userPrompt, setUserPrompt] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [regenerateMessageId, setRegenerateMessageId] = useState(null);
    const [loadingStartTime, setLoadingStartTime] = useState(null);

    // Refs
    const scrollRef = useRef(null);
    const formRef = useRef(null);
    const textareaRef = useRef(null);

    // Auto-resize textarea
    const adjustTextareaHeight = () => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = 'auto';
            textarea.style.height = `${textarea.scrollHeight}px`;
            textarea.style.overflowY = textarea.scrollHeight > 200 ? 'auto' : 'hidden';
        }
    };

    // Load conversation
    useEffect(() => {
        if (id) {
            const getConversation = async () => {
                try {
                    const data = await conversation(token, id);
                    setMessages(data.mapping ? Object.values(data.mapping) : []);
                } catch (error) {
                    console.error('Failed to fetch conversation:', error);
                    setMessages([]);
                }

                if (scrollRef.current) {
                    scrollRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            };
            getConversation();
        }
    }, [id, token, regenerateMessageId, conversationsUpdated]);

    // Auto-scroll
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);

    // Create user message
    const createUserMessage = (queryText) => {
        const timestamp = new Date().getTime();
        const id = `user-${timestamp}`;

        return {
            id,
            message: {
                id,
                author: { role: "user" },
                create_time: new Date().toISOString(),
                update_time: new Date().toISOString(),
                content: {
                    content_type: "text",
                    parts: [queryText]
                },
            },
            parent: null,
            children: []
        };
    };

    // Handle message submission
    const handleSubmit = async (event, query = null, messageId = null, parentId = null) => {
        if (event && event.preventDefault) {
            event.preventDefault();
        }

        query = query ?? userPrompt.trim();
        if (!query) return;

        // Prevent multiple concurrent requests
        if (isLoading) {
            return;
        }

        setIsLoading(true);
        setLoadingStartTime(Date.now());
        setUserPrompt('');

        const messageInput = formRef.current?.elements.messageInput;
        if (!messageInput) {
            console.error('Message input not found');
            setIsLoading(false);
            return;
        }

        const messageValue = query || messageInput.value;
        const userMessage = createUserMessage(userPrompt);

        if (messageId) {
            setRegenerateMessageId(messageId);
        } else {
            setMessages(prevMessages => {
                const newMessages = [...prevMessages, userMessage];

                // Add empty assistant message that will be updated with response
                const assistantMessage = {
                    id: `assistant-${Date.now()}`,
                    message: {
                        id: `assistant-${Date.now()}`,
                        author: { role: "assistant" },
                        create_time: new Date().toISOString(),
                        update_time: new Date().toISOString(),
                        content: {
                            content_type: "text",
                            parts: [""]
                        },
                    },
                    parent: null,
                    children: []
                };

                return [...newMessages, assistantMessage];
            });
        }

        try {
            // 🚀 SIMPLE SSE - Show content immediately as it arrives
            await fetchEventSource(config.API_BASE_URL + 'v1/conversation/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${token}`,
                    'Accept': 'text/event-stream',
                },
                body: JSON.stringify({
                    message: messageValue,
                    parent_id: parentId || null,
                    session_id: id || null,
                    message_id: messageId || null
                }),

                onmessage(event) {
                    try {
                        const parsed = JSON.parse(event.data);

                        if (parsed.status === 'DONE') {
                            if (parsed.conversation_id && parsed.conversation_id !== id) {
                                navigate(`/c/${parsed.conversation_id}`);
                            }
                            return;
                        }

                        // Update UI immediately with content (no throttling)
                        if (parsed.message?.content?.parts) {
                            const content = parsed.message.content.parts[0];
                            
                            setMessages(prevMessages => {
                                return prevMessages.map((msg, index) => {
                                    // Update the LAST assistant message (the one being streamed)
                                    const isLastAssistantMessage = msg.message?.author?.role === 'assistant' && 
                                                                  index === prevMessages.length - 1;
                                    
                                    if (isLastAssistantMessage) {
                                        return {
                                            ...msg,
                                            id: parsed.id || msg.id,
                                            message: {
                                                ...msg.message,
                                                id: parsed.id || msg.message.id,
                                                content: {
                                                    ...msg.message.content,
                                                    parts: [content]
                                                }
                                            },
                                            parent: parsed.parent || msg.parent
                                        };
                                    }
                                    return msg;
                                });
                            });
                        }
                    } catch (parseError) {
                        console.error('Error parsing SSE message:', parseError);
                    }
                },

                onopen(response) {
                    if (response.ok && response.status === 200) {
                        return;
                    } else {
                        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                    }
                },

                onerror(err) {
                    console.error('SSE connection error:', err);
                    throw err;
                }
            });

        } catch (error) {
            console.error('Failed to send message:', error);
        } finally {
            const minLoadingTime = MIN_LOADING_TIME_MS;
            const elapsedTime = loadingStartTime ? Date.now() - loadingStartTime : 0;
            const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

            setTimeout(() => {
                setIsLoading(false);
                setLoadingStartTime(null);
                setRegenerateMessageId(null);
                // Only refresh conversations if this was a regeneration
                if (messageId) {
                    refreshConversations();
                }
            }, remainingTime);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSubmit(event);
        }
    };

    return {
        // State
        messages,
        userPrompt,
        isLoading,
        regenerateMessageId,

        // Setters
        setUserPrompt,

        // Refs
        scrollRef,
        formRef,
        textareaRef,

        // Functions
        handleSubmit,
        handleKeyDown,
        adjustTextareaHeight
    };
};